var __esDecorate = (this && this.__esDecorate) || function (ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) { if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected"); return f; }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for (var i = decorators.length - 1; i >= 0; i--) {
        var context = {};
        for (var p in contextIn) context[p] = p === "access" ? {} : contextIn[p];
        for (var p in contextIn.access) context.access[p] = contextIn.access[p];
        context.addInitializer = function (f) { if (done) throw new TypeError("Cannot add initializers after decoration has completed"); extraInitializers.push(accept(f || null)); };
        var result = (0, decorators[i])(kind === "accessor" ? { get: descriptor.get, set: descriptor.set } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        }
        else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
};
var __runInitializers = (this && this.__runInitializers) || function (thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for (var i = 0; i < initializers.length; i++) {
        value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    }
    return useValue ? value : void 0;
};
var __setFunctionName = (this && this.__setFunctionName) || function (f, name, prefix) {
    if (typeof name === "symbol") name = name.description ? "[".concat(name.description, "]") : "";
    return Object.defineProperty(f, "name", { configurable: true, value: prefix ? "".concat(prefix, " ", name) : name });
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import styles from './sds-fieldset.lit.scss';
import stylesGlobal from './_sds-fieldset-global.lit.scss';
import { appendStylesToTheStyleRoot } from '../utilities/dom';
import { sdsCustomElement } from '../utilities/custom-components';
/**
 * `sds-fieldset` is a component that groups related form elements together under a common title.
 * This component is useful for organizing complex forms by visually separating fields into distinct
 * sections, improving user experience and readability.
 *
 * The `fieldset-title` attribute allows you to set a custom title for the fieldset, giving context to the
 * grouped fields and aiding in form structure.
 *
 * ### Attributes
 * - `fieldset-title`: A string that specifies the title displayed at the top of the fieldset.
 *   This title helps users understand the purpose of the grouped fields.
 *
 * ### Example Usage
 * ```html
 * <sds-fieldset fieldset-title="Personal Information">
 *   <!-- Form fields go here -->
 * </sds-fieldset>
 * ```
 *
 * @tag sds-fieldset
 *
 * @attr {string} fieldset-title - The title text for the fieldset, displayed at the top of the grouped fields.
 */
let SdsFieldset = (() => {
    var _SdsFieldset_title_accessor_storage;
    let _classDecorators = [sdsCustomElement('sds-fieldset')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    let _classSuper = LitElement;
    let _title_decorators;
    let _title_initializers = [];
    let _title_extraInitializers = [];
    var SdsFieldset = _classThis = class extends _classSuper {
        /**
         * Sets the title text displayed at the top of the fieldset, providing context for the grouped fields.
         * This attribute enhances form organization by labeling sections, making forms easier to navigate.
         *
         * Use this attribute to give users a clear understanding of the type of information
         * expected within this section of the form.
         *
         * @attr {string} fieldset-title - The title text for the fieldset, helping to define the purpose of the grouped fields.
         */
        get title() { return __classPrivateFieldGet(this, _SdsFieldset_title_accessor_storage, "f"); }
        set title(value) { __classPrivateFieldSet(this, _SdsFieldset_title_accessor_storage, value, "f"); }
        render() {
            appendStylesToTheStyleRoot(this, 'sds-fieldset', this.stylesGlobal.cssText);
            return html `
            <div class="sds-fieldset">
                ${this.renderTitle()}
                <slot></slot>
            </div>
        `;
        }
        renderTitle() {
            if (this.title) {
                return html ` <div class="sds-fieldset__title">${this.title}</div> `;
            }
        }
        constructor() {
            super(...arguments);
            this.stylesGlobal = stylesGlobal;
            _SdsFieldset_title_accessor_storage.set(this, __runInitializers(this, _title_initializers, void 0));
            __runInitializers(this, _title_extraInitializers);
        }
    };
    _SdsFieldset_title_accessor_storage = new WeakMap();
    __setFunctionName(_classThis, "SdsFieldset");
    (() => {
        var _a;
        const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create((_a = _classSuper[Symbol.metadata]) !== null && _a !== void 0 ? _a : null) : void 0;
        _title_decorators = [property({ type: String, attribute: 'fieldset-title' })];
        __esDecorate(_classThis, null, _title_decorators, { kind: "accessor", name: "title", static: false, private: false, access: { has: obj => "title" in obj, get: obj => obj.title, set: (obj, value) => { obj.title = value; } }, metadata: _metadata }, _title_initializers, _title_extraInitializers);
        __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
        SdsFieldset = _classThis = _classDescriptor.value;
        if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
    })();
    _classThis.styles = styles;
    (() => {
        __runInitializers(_classThis, _classExtraInitializers);
    })();
    return SdsFieldset = _classThis;
})();
export { SdsFieldset };
